/**
 * HTML Sanitization Utility
 * 
 * Provides safe HTML sanitization using DOMPurify to prevent XSS attacks.
 * All user-generated content should be sanitized before rendering with dangerouslySetInnerHTML.
 */

import DOMPurify from 'dompurify';

const DANGEROUS_STYLE = /url\s*\(|expression\s*\(|javascript:|@import|behavior\s*:|-moz-binding/i;

function hardenSanitizedHtml(dirty, options) {
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "A") {
      const href = node.getAttribute("href") || "";
      if (/^\s*(?:javascript|data):/i.test(href)) node.removeAttribute("href");
      if (node.getAttribute("target") === "_blank") {
        node.setAttribute("rel", "noopener noreferrer");
      }
    }
    if (node.hasAttribute("style") && DANGEROUS_STYLE.test(node.getAttribute("style") || "")) {
      node.removeAttribute("style");
    }
  });
  try {
    return DOMPurify.sanitize(dirty, options);
  } finally {
    DOMPurify.removeHook("afterSanitizeAttributes");
  }
}

/**
 * Sanitizes HTML content to prevent XSS attacks
 * @param {string} dirty - Unsanitized HTML string
 * @param {Object} options - DOMPurify configuration options
 * @returns {string} Sanitized HTML string safe for dangerouslySetInnerHTML
 */
export const sanitizeHTML = (dirty, options = {}) => {
  if (!dirty) return '';
  
  // Default configuration - strict security
  const defaultOptions = {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'blockquote', 'pre', 'code', 'a', 'img',
      'div', 'span', 'section', 'article', 'header', 'footer'
    ],
    ALLOWED_ATTR: [
      'href', 'title', 'alt', 'src', 'class', 'id', 'style',
      'target', 'rel'
    ],
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp|data):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
    KEEP_CONTENT: true,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    RETURN_TRUSTED_TYPE: false,
    SAFE_FOR_TEMPLATES: false,
    SANITIZE_DOM: true,
    ...options
  };

  try {
    return hardenSanitizedHtml(dirty, defaultOptions);
  } catch (error) {
    console.error('Error sanitizing HTML:', error);
    // Return empty string on error to be safe
    return '';
  }
};

/**
 * Sanitizes HTML for rich text content (more permissive)
 * Allows more formatting options for content created with rich text editors
 * @param {string} dirty - Unsanitized HTML string
 * @returns {string} Sanitized HTML string
 */
export const sanitizeRichText = (dirty) => {
  if (!dirty) return '';
  
  const richTextOptions = {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 's', 'strike', 'sub', 'sup',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'blockquote', 'pre', 'code',
      'a', 'img', 'div', 'span', 'section', 'article',
      'table', 'thead', 'tbody', 'tr', 'td', 'th',
      'hr', 'b', 'i', 'small', 'mark', 'del', 'ins'
    ],
    ALLOWED_ATTR: [
      'href', 'title', 'alt', 'src', 'class', 'id', 'style',
      'target', 'rel', 'width', 'height', 'colspan', 'rowspan',
      'align', 'valign'
    ],
    ALLOW_DATA_ATTR: false,
    ALLOW_UNKNOWN_PROTOCOLS: false
  };

  try {
    return hardenSanitizedHtml(dirty, richTextOptions);
  } catch (error) {
    console.error('Error sanitizing rich text:', error);
    return '';
  }
};

/**
 * Sanitizes plain text (removes all HTML)
 * Use this for user inputs that should be plain text only
 * @param {string} dirty - HTML string to convert to plain text
 * @returns {string} Plain text with all HTML removed
 */
export const sanitizeToPlainText = (dirty) => {
  if (!dirty) return '';
  
  try {
    return DOMPurify.sanitize(dirty, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
      KEEP_CONTENT: true
    });
  } catch (error) {
    console.error('Error sanitizing to plain text:', error);
    return '';
  }
};

/**
 * Creates a sanitized object for dangerouslySetInnerHTML
 * @param {string} html - HTML string to sanitize
 * @param {boolean} richText - Whether to use rich text sanitization (more permissive)
 * @returns {Object} Object with __html property safe for dangerouslySetInnerHTML
 */
export const createSanitizedHTML = (html, richText = false) => {
  const sanitized = richText ? sanitizeRichText(html) : sanitizeHTML(html);
  return { __html: sanitized };
};

const sanitizeModule = {
  sanitizeHTML,
  sanitizeRichText,
  sanitizeToPlainText,
  createSanitizedHTML
};

export default sanitizeModule;

